'use client';

import makeId from '@/utils/makeId';
import { normalizeMapEmbed } from '@/utils/normalizeMapEmbed';
import React from 'react';
import versionInfo from '../editor/version.js';
import { getColorScheme, getMapColorScheme } from '../utils/getColorScheme';
import { getVerticalSpacing } from '../utils/getVerticalSpacing';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ helpers ------------------------------ */

function handleScrollToPageTop(event) {
    event.preventDefault();
    const ownerWindow = event.currentTarget.ownerDocument?.defaultView;
    ownerWindow?.scrollTo({ top: 0, behavior: 'smooth' });
}

const DEFAULT_FOOTER_MEDIA_SIZE = 120;

function stripTags(html = '') {
    return String(html).replace(/<[^>]*>/g, '');
}

function stripLinksKeepMdAndHtml(src = '') {
    let s = String(src);
    s = s.replace(/\[([^\]]+)\]\((?:mailto:|tel:)?[^)]+\)/gi, '$1');
    s = s.replace(/<\/?a\b[^>]*>/gi, '');
    return s;
}

function extractEmail(raw = '') {
    const s = stripTags(raw);
    const m = s.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    return m ? m[0] : '';
}

function buildTel(raw = '') {
    const txt = stripTags(raw);
    return txt.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
}

function isEmailText(raw = '') {
    const txt = stripTags(String(raw))
        .replace(/[*_`~]/g, '')
        .trim();
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(txt);
}

function hasText(value) {
    return String(value ?? '').trim().length > 0;
}

function coerceBoolean(value) {
    return typeof value === 'string'
        ? value.toLowerCase().trim() === 'true'
        : Boolean(value);
}

function isExternalLink(href = '') {
    return /^(?:https?:)?\/\//i.test(String(href));
}

function toPositiveInt(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return null;
    return Math.round(numeric);
}

function getMediaIntrinsicSize(media) {
    if (!media || typeof media !== 'object') {
        return { width: null, height: null };
    }

    const width = toPositiveInt(
        media.Width ??
            media.width ??
            media.MediaWidth ??
            media.mediaWidth ??
            media.w
    );
    const height = toPositiveInt(
        media.Height ??
            media.height ??
            media.MediaHeight ??
            media.mediaHeight ??
            media.h
    );

    if (width && height) return { width, height };
    return { width: null, height: null };
}

function normalizeMediaUrl(input) {
    const raw =
        typeof input === 'string'
            ? input
            : input && typeof input === 'object'
            ? input.Media || input.media || input.url || input.src || ''
            : '';
    const value = String(raw || '').trim();
    if (!value) return '';
    if (
        /^https?:\/\//i.test(value) ||
        value.startsWith('data:') ||
        value.startsWith('/')
    ) {
        return value;
    }
    if (value.startsWith('images/')) return `/${value}`;
    return `/images/${value.split('/').pop()}`;
}

function isPlaceholderMediaUrl(url = '') {
    const normalized = String(url || '')
        .trim()
        .split(/[?#]/)[0]
        .replace(/^\.?\//, '/');
    return normalized === '/images/placeholder.svg';
}

function getTopOnlySpacingClasses(spacing) {
    return getVerticalSpacing(spacing || 'none')
        .split(/\s+/)
        .filter(Boolean)
        .flatMap((token) => {
            const parts = token.split(':');
            const base = parts.pop() || '';
            const prefix = parts.length ? `${parts.join(':')}:` : '';
            if (base.startsWith('pb-')) return [];
            if (base.startsWith('py-')) return [`${prefix}pt-${base.slice(3)}`];
            if (base.startsWith('p-')) {
                return [`${prefix}${base.replace(/^p-/, 'pt-')}`];
            }
            return [`${prefix}${base}`];
        })
        .join(' ')
        .trim();
}

function getBottomOnlySpacingClasses(spacing) {
    return getVerticalSpacing(spacing || 'none')
        .split(/\s+/)
        .filter(Boolean)
        .flatMap((token) => {
            const parts = token.split(':');
            const base = parts.pop() || '';
            const prefix = parts.length ? `${parts.join(':')}:` : '';
            if (base.startsWith('pt-')) return [];
            if (base.startsWith('py-')) return [`${prefix}pb-${base.slice(3)}`];
            if (base.startsWith('p-')) {
                return [`${prefix}${base.replace(/^p-/, 'pb-')}`];
            }
            return [`${prefix}${base}`];
        })
        .join(' ')
        .trim();
}

/* ------------------------------ component ------------------------------ */

export default function Footer(props) {
    const {
        Medias,
        ServicesHeader,
        LinksHeader,
        Links,
        NavLinks,
        SyncLinksWithNav = false,
        InfoHeader,
        Header,
        Email,
        Phones,
        TextField,
        Address,
        Socials,
        GoogleMap = '',
        MapColorScheme = 'light',
        Body,
        Services,
        NavServices,
        SyncServicesWithNav = false,
        AdditionalDropdowns = [],
        BusinessHour,
        License,
        ColorScheme = 'light',
        Alignment = 'left',
        VerticalSpacing = 'none',
        BottomRowSpacing = 'none',
        Engage = 'left',
        CreditCards = '',
        PolicyLinks = [{ Name: 'Privacy Policy', Link: '/privacy' }],
        CopyrightText = 'Copyright {year}',
        EmailLabel = 'Email Us',
    } = props;

    const resolvedCopyrightText = String(CopyrightText ?? '')
        .replace(/\{year\}/gi, String(new Date().getFullYear()))
        .trim();

    const normalizedColorScheme = String(ColorScheme ?? '').trim();
    const colorSchemeToUse = normalizedColorScheme.startsWith('box')
        ? 'white'
        : normalizedColorScheme || 'white';

    const {
        backgroundColor,
        headerColor,
        textColor,
        iconColor,
        linkColor,
        backgroundStyle,
    } = getColorScheme(colorSchemeToUse, props.GradientDirection);

    const { mapBackgroundClass, mapBlendEffect } =
        getMapColorScheme(MapColorScheme);

    const creditCardColor = `${textColor} opacity-30`;

    const isDarkBackground =
        (/gradient/.test(backgroundColor) &&
            !colorSchemeToUse.startsWith('light-gradient') &&
            !colorSchemeToUse.startsWith('light-primary-gradient') &&
            !colorSchemeToUse.startsWith('light-secondary-gradient')) ||
        /-(6|7|8|9)00\b/.test(backgroundColor) ||
        colorSchemeToUse.startsWith('mid');

    const brandBrightness =
        colorSchemeToUse.startsWith('mid') ||
        colorSchemeToUse.startsWith('light-primary-gradient') ||
        colorSchemeToUse.startsWith('light-secondary-gradient') ||
        colorSchemeToUse.startsWith('darker') ||
        colorSchemeToUse.startsWith('darker-secondary') ||
        isDarkBackground
            ? 'invert grayscale brightness-0'
            : 'hover:opacity-80';

    // Layout / alignment
    let gridAlign = 'gap-6 md:col-span-2 md:grid md:grid-cols-2 lg:grid-cols-2';
    let mainGridAlign = 'lg:gap-8 lg:grid lg:grid-cols-3 ';
    let textAlign = '';
    let navGrid =
        'md:grid-cols-3 lg:col-span-2 lg:pl-6 xl:pl-10 2xl:pl-24 grid-cols-2';
    let serviceCol = undefined;
    let mapAlignment = 'h-[325px]';
    let itemPadding;
    let brandWrap;
    let menuWrap;

    if (Alignment === 'center') {
        gridAlign = 'gap-6 col-span-2 md:grid md:grid-cols-2 lg:grid-cols-2';
        mainGridAlign = 'grid grid-cols-1';
        textAlign = 'text-center justify-center mx-auto';
        navGrid =
            'md:grid-cols-3 lg:mx-auto lg:px-24 xl:col-span-2 grid-cols-2';
        serviceCol = 'col-span-2 md:col-span-1';
        mapAlignment = 'mx-auto h-[500px]';
        itemPadding = 'px-4';
    } else if (Alignment === 'right') {
        mainGridAlign = 'lg:gap-8 lg:grid lg:grid-cols-3';
        textAlign = 'lg:text-left';
        navGrid =
            'grid-cols-2 md:grid-cols-3 lg:col-span-2 lg:pr-6 xl:pr-10 2xl:pr-24';
        mapAlignment = 'h-[325px] lg:ml-auto';
        itemPadding = 'px-0';
        brandWrap =
            'lg:order-2 lg:col-span-1 lg:justify-self-end lg:text-right';
        menuWrap = 'lg:order-1 lg:col-span-2';
    }

    const vSpace = `${getTopOnlySpacingClasses(VerticalSpacing)} pb-10`.trim();
    const bottomRowSpacing = getBottomOnlySpacingClasses(BottomRowSpacing);

    const SOCIAL_ICON_MAP = {
        email: 'fa-solid fa-envelope',
        mail: 'fa-solid fa-envelope',
        phone: 'fa-solid fa-phone',
        tel: 'fa-solid fa-phone',
        linkedin: 'fa-brands fa-linkedin-in',
        facebook: 'fa-brands fa-facebook',
        instagram: 'fa-brands fa-instagram',
        twitter: 'fa-brands fa-x-twitter',
        x: 'fa-brands fa-x-twitter',
        google: 'fa-brands fa-google',
        youtube: 'fa-brands fa-youtube',
        pinterest: 'fa-brands fa-pinterest',
        whatsapp: 'fa-brands fa-whatsapp',
        sms: 'fa-solid fa-message-sms',
        tiktok: 'fa-brands fa-tiktok',
        yelp: 'fa-brands fa-yelp',
        houzz: 'fa-brands fa-houzz',
        mobilo: 'fa-kit fa-mobilo',
        flickr: 'fa-brands fa-flickr',
        angi: 'fa-kit fa-angi',
        angislist: 'fa-kit fa-angi',
        angieslist: 'fa-kit fa-angi',
        bbb: 'fa-kit fa-bbb',
        betterbusinessbureau: 'fa-kit fa-bbb',
        capterra: 'fa-kit fa-capterra',
        homestars: 'fa-kit fa-homestars',
        nextdoor: 'fa-kit fa-nextdoor',
        nicejob: 'fa-kit fa-nicejob',
        thumbtack: 'fa-kit fa-thumbtack',
        yellowpages: 'fa-kit fa-yellowpages',
        yp: 'fa-kit fa-yellowpages',
    };

    const normalizeNetwork = (network) =>
        String(network || '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '');

    function iconLookup(network) {
        if (typeof network === 'string' && network.trim().startsWith('<svg')) {
            return (
                <div
                    className="flex items-center justify-center"
                    dangerouslySetInnerHTML={{
                        __html: network.replace(
                            '<svg',
                            '<svg class="h-9 w-8 text-current" fill="currentColor"'
                        ),
                    }}
                />
            );
        }
        const key = normalizeNetwork(network);
        const iconClass = SOCIAL_ICON_MAP[key];
        if (!iconClass) return null;
        return <i className={iconClass} />;
    }

    function creditCardIconLookup(card) {
        switch (String(card || '').toLowerCase()) {
            case 'amex':
                return (
                    <i className={`fa-brands fa-cc-amex ${creditCardColor}`} />
                );
            case 'mastercard':
                return (
                    <i
                        className={`fa-brands fa-cc-mastercard ${creditCardColor}`}
                    />
                );
            case 'visa':
                return (
                    <i className={`fa-brands fa-cc-visa ${creditCardColor}`} />
                );
            case 'discover':
                return (
                    <i
                        className={`fa-brands fa-cc-discover ${creditCardColor}`}
                    />
                );
            case 'paypal':
                return (
                    <i
                        className={`fa-brands fa-cc-paypal ${creditCardColor}`}
                    />
                );
            case 'applepay':
                return (
                    <i
                        className={`fa-brands fa-apple-pay ${creditCardColor}`}
                    />
                );
            case 'googlepay':
                return (
                    <i
                        className={`fa-brands fa-google-pay ${creditCardColor}`}
                    />
                );
            case 'stripe':
                return (
                    <i className={`fa-brands fa-stripe ${creditCardColor}`} />
                );
            case 'jcb':
                return (
                    <i className={`fa-brands fa-cc-jcb ${creditCardColor}`} />
                );
            case 'dinersclub':
                return (
                    <i
                        className={`fa-brands fa-cc-diners-club ${creditCardColor}`}
                    />
                );
            case 'bitcoin':
                return (
                    <i className={`fa-brands fa-bitcoin ${creditCardColor}`} />
                );
            case 'cash':
                return <i className={`fa-kit fa-cash ${creditCardColor}`} />;
            case 'zelle':
                return <i className={`fa-kit fa-zelle ${creditCardColor}`} />;
            default:
                return null;
        }
    }

    const creditCardArray = Array.isArray(CreditCards)
        ? CreditCards.map((c) => String(c || '').trim()).filter(Boolean)
        : CreditCards
        ? String(CreditCards)
              .split(',')
              .map((c) => c.trim())
              .filter(Boolean)
        : [];

    const inferKeyFromLink = (link) => {
        const s = String(link || '')
            .toLowerCase()
            .trim();
        if (!s) return '';
        if (s.startsWith('mailto:') || s.includes('@')) return 'email';
        if (s.startsWith('tel:')) return 'phone';
        if (s.startsWith('sms:')) return 'sms';
        if (/^\+?\d[\d\s().-]*$/.test(s)) return 'phone';
        return '';
    };

    const socialsList = (() => {
        if (!Array.isArray(Socials)) return [];
        const seen = new Set();
        const list = [];
        Socials.forEach((s) => {
            const url = s?.Link || s?.URL || s?.Url || s?.Href || s?.link || '';
            const name = s?.Name || s?.Label || s?.Title || s?.name || 'Social';
            const normName = normalizeNetwork(name);
            const inferred = inferKeyFromLink(url);
            const key = normName || inferred;
            const finalKey = key || normalizeNetwork(name || url);
            if (finalKey && seen.has(finalKey)) return;
            seen.add(finalKey);
            const trimmedUrl = String(url).trim();
            if (!trimmedUrl) return;
            list.push({
                url: trimmedUrl,
                name: String(name).trim() || finalKey,
            });
        });
        return list;
    })();

    const mdLink = ({ href, title, children, className, ...rest }) => {
        if (!hasText(href)) {
            return <span>{children}</span>;
        }

        const openNew = title === 'newtab' || isExternalLink(href);

        return (
            <a
                {...rest}
                href={String(href).trim()}
                title={title}
                target={openNew ? '_blank' : undefined}
                rel={openNew ? 'noopener noreferrer' : undefined}
                className={`${
                    className ? `${className} ` : ''
                }${linkColor} font-normal no-underline transition-all ease-in`}
            >
                {children}
            </a>
        );
    };

    const linkSource =
        coerceBoolean(SyncLinksWithNav) && Array.isArray(NavLinks)
            ? NavLinks
            : Links;
    const footerLinks = Array.isArray(linkSource)
        ? linkSource.filter(({ Name, Link }) => hasText(Name) && hasText(Link))
        : [];

    const serviceSource =
        coerceBoolean(SyncServicesWithNav) && Array.isArray(NavServices)
            ? NavServices
            : Services;
    const serviceLinks = Array.isArray(serviceSource)
        ? serviceSource.filter(
              ({ Name, Anchor }) => hasText(Name) && hasText(Anchor)
          )
        : [];
    const additionalDropdowns = Array.isArray(AdditionalDropdowns)
        ? AdditionalDropdowns.map((menu) => ({
              ...menu,
              Items: Array.isArray(menu?.Items)
                  ? menu.Items.filter(
                        ({ Name, Anchor, Link }) =>
                            hasText(Name) && hasText(Anchor || Link)
                    )
                  : [],
          })).filter((menu) => hasText(menu?.Header) && menu.Items.length > 0)
        : [];

    const policyLinks = Array.isArray(PolicyLinks)
        ? PolicyLinks.filter(({ Name, Link }) => hasText(Name) && hasText(Link))
        : [];

    const footerMedias = Array.isArray(Medias)
        ? Medias.filter((media) => {
              const mediaUrl = normalizeMediaUrl(media);
              return hasText(mediaUrl) && !isPlaceholderMediaUrl(mediaUrl);
          })
        : [];

    const emailLabel =
        typeof EmailLabel === 'string' && EmailLabel.trim()
            ? EmailLabel
            : isEmailText(Email)
            ? 'Email Us'
            : stripLinksKeepMdAndHtml(Email);
    // md renderer for UL; keep signature to match renderer expectations, drop unused props
    const mdUl = ({ className, ordered, node, ...rest }) => {
        // react-markdown hands these down for <ul>; mark as used so lint stays quiet
        void ordered;
        void node;
        return (
            <ul
                {...rest}
                className={`space-y-4${className ? ` ${className}` : ''}`}
            />
        );
    };

    return (
        <footer
            id="footer"
            className={`editable-component relative ${backgroundColor} px-6 lg:px-8 ${vSpace}`}
            style={backgroundStyle}
            data-json="footer"
            data-editor-version={versionInfo.version}
        >
            <div className="relative z-40 mx-auto max-w-7xl pt-6">
                <div className={`${mainGridAlign} ${bottomRowSpacing}`.trim()}>
                    <div className={brandWrap || ''}>
                        {footerMedias.length > 0 && (
                            <div
                                className={`mb-8 flex ${textAlign} max-w-2xl gap-6`}
                            >
                                {footerMedias.slice(0, 3).map((m, i) => {
                                    const { width, height } =
                                        getMediaIntrinsicSize(m);
                                    const mediaUrl = normalizeMediaUrl(m);
                                    const altText =
                                        String(m?.AltText || '').trim() ||
                                        'Footer logo';
                                    const intrinsicProps = {
                                        width:
                                            width || DEFAULT_FOOTER_MEDIA_SIZE,
                                        height:
                                            height || DEFAULT_FOOTER_MEDIA_SIZE,
                                    };
                                    const mediaClassName =
                                        width && height
                                            ? 'h-auto max-h-[120px] w-auto max-w-full object-contain'
                                            : 'h-[120px] max-w-full object-contain';
                                    return (
                                        <div
                                            key={i}
                                            className={`flex max-w-80 items-center justify-center ${
                                                itemPadding || ''
                                            }`}
                                        >
                                            <img
                                                src={mediaUrl}
                                                alt={altText}
                                                {...intrinsicProps}
                                                loading="lazy"
                                                decoding="async"
                                                className={mediaClassName}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {Header && (
                            <MarkDown
                                variant="h2"
                                className={`font-display text-2xl tracking-tight ${textAlign} ${headerColor} md:text-3xl lg:text-4xl`}
                                components={{ a: mdLink }}
                            >
                                {Header}
                            </MarkDown>
                        )}

                        {GoogleMap && (
                            <div
                                className={`my-12 max-w-3xl overflow-hidden rounded-[min(1.5rem,var(--shape-radius))] ${mapAlignment} ${mapBackgroundClass} shadow-md`}
                            >
                                <iframe
                                    title="Embedded map"
                                    src={normalizeMapEmbed(GoogleMap)}
                                    className={`h-full w-full ${mapBlendEffect} border-0`}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        )}

                        {Body && (
                            <MarkDown
                                className={`mt-6 ${textAlign} font-text leading-7 ${textColor} max-w-3xl`}
                                components={{ a: mdLink }}
                            >
                                {Body}
                            </MarkDown>
                        )}

                        {socialsList.length > 0 && (
                            <div
                                className={`mt-6 flex space-x-6 text-3xl ${
                                    Alignment === 'center'
                                        ? 'justify-center'
                                        : 'justify-start'
                                }`}
                            >
                                {socialsList.map((s) => (
                                    <a
                                        key={s.url}
                                        href={s.url}
                                        className={`${linkColor} font-normal no-underline transition-all ease-in`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <span className="sr-only">
                                            {s.name}
                                        </span>
                                        <span>
                                            {iconLookup(s.name) || (
                                                <span className="font-semibold">
                                                    {s.name.charAt(0)}
                                                </span>
                                            )}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        )}

                        {creditCardArray.length > 0 && (
                            <div
                                className={`mt-6 flex flex-wrap gap-6 text-3xl ${
                                    Alignment === 'center'
                                        ? 'justify-center'
                                        : 'justify-start'
                                }`}
                            >
                                {creditCardArray.map((card, idx) => (
                                    <span key={`${card}-${idx}`}>
                                        {creditCardIconLookup(card)}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer menu */}
                    <div
                        className={`mt-12 grid place-content-evenly gap-8 lg:w-full lg:gap-10 ${
                            textAlign || ''
                        } ${navGrid} ${menuWrap || ''}`}
                    >
                        {/* Our Services */}
                        <div className={`w-full ${serviceCol || ''}`}>
                            <div
                                className={`font-display text-lg leading-6 ${headerColor}`}
                            >
                                <MarkDown components={{ a: mdLink }}>
                                    {ServicesHeader}
                                </MarkDown>
                            </div>
                            {serviceLinks.length > 0 && (
                                <MarkDown
                                    className="mt-6 text-sm font-text leading-6"
                                    components={{ a: mdLink, ul: mdUl }}
                                >
                                    {serviceLinks
                                        .map(
                                            (s) =>
                                                `- [${String(
                                                    s.Name
                                                ).trim()}](${makeId(
                                                    String(s.Anchor).trim()
                                                )})`
                                        )
                                        .join('\n')}
                                </MarkDown>
                            )}
                        </div>

                        {additionalDropdowns.map((menu, index) => (
                            <div
                                key={`footer-additional-dropdown-${index}`}
                                className="w-full"
                            >
                                <div
                                    className={`font-display text-lg leading-6 ${headerColor}`}
                                >
                                    <MarkDown components={{ a: mdLink }}>
                                        {menu.Header}
                                    </MarkDown>
                                </div>
                                <MarkDown
                                    className="mt-6 text-sm font-text leading-6"
                                    components={{ a: mdLink, ul: mdUl }}
                                >
                                    {menu.Items.map(
                                        ({ Name, Anchor, Link }) =>
                                            `- [${String(Name).trim()}](${
                                                Anchor
                                                    ? makeId(
                                                          String(Anchor).trim()
                                                      )
                                                    : String(Link).trim()
                                            })`
                                    ).join('\n')}
                                </MarkDown>
                            </div>
                        ))}

                        {/* Learn More + Get in Touch */}
                        <div className={gridAlign}>
                            <div className="lg:px-3">
                                <div
                                    className={`font-display text-lg leading-6 ${headerColor}`}
                                >
                                    <MarkDown components={{ a: mdLink }}>
                                        {LinksHeader}
                                    </MarkDown>
                                </div>
                                {footerLinks.length > 0 && (
                                    <MarkDown
                                        className="mt-6 text-sm font-text leading-6"
                                        components={{ a: mdLink, ul: mdUl }}
                                    >
                                        {footerLinks
                                            .map(
                                                ({ Name, Link }) =>
                                                    `- [${String(
                                                        Name
                                                    ).trim()}](${String(
                                                        Link
                                                    ).trim()})`
                                            )
                                            .join('\n')}
                                    </MarkDown>
                                )}
                            </div>

                            {/* Get in Touch */}
                            <div className="md:grid md:grid-cols-1 md:gap-8">
                                <div className="mt-10 md:mt-0">
                                    <div
                                        className={`font-display text-lg leading-6 ${headerColor}`}
                                    >
                                        <MarkDown components={{ a: mdLink }}>
                                            {InfoHeader}
                                        </MarkDown>
                                    </div>

                                    <ul className="mt-6 space-y-4">
                                        {Email && (
                                            <li>
                                                <a
                                                    href={`mailto:${extractEmail(
                                                        Email
                                                    )}`}
                                                    className={`text-sm font-text leading-6 ${linkColor} font-normal no-underline transition-all ease-in`}
                                                >
                                                    <MarkDown
                                                        components={{
                                                            p: 'span',
                                                            a: 'span',
                                                        }}
                                                    >
                                                        {emailLabel}
                                                    </MarkDown>
                                                </a>
                                            </li>
                                        )}

                                        {Array.isArray(Phones) &&
                                            Phones.map((p, i) => {
                                                const visible = String(
                                                    p?.Number ?? ''
                                                ).trim();
                                                const hrefTel =
                                                    buildTel(visible);
                                                if (!hrefTel) return null;
                                                const phoneLabel =
                                                    stripLinksKeepMdAndHtml(
                                                        visible
                                                    ).trim() || hrefTel;
                                                return (
                                                    <li key={`phone-${i}`}>
                                                        <a
                                                            href={`tel:${hrefTel}`}
                                                            aria-label={`Call ${phoneLabel}`}
                                                            className={`text-sm font-text leading-6 ${linkColor} font-normal no-underline transition-all ease-in`}
                                                        >
                                                            <MarkDown
                                                                components={{
                                                                    p: 'span',
                                                                    a: 'span',
                                                                }}
                                                            >
                                                                {phoneLabel}
                                                            </MarkDown>
                                                        </a>
                                                    </li>
                                                );
                                            })}

                                        {Array.isArray(TextField) &&
                                            TextField.filter(
                                                (t) =>
                                                    hasText(t.Link) &&
                                                    hasText(t.Label)
                                            ).map((t, i) => (
                                                <li key={`txt-${i}`}>
                                                    <a
                                                        href={String(
                                                            t.Link
                                                        ).trim()}
                                                        className={`text-sm font-text leading-6 ${linkColor} font-normal no-underline transition-all ease-in`}
                                                    >
                                                        <MarkDown
                                                            components={{
                                                                p: 'span',
                                                                a: 'span',
                                                            }}
                                                        >
                                                            {stripLinksKeepMdAndHtml(
                                                                String(
                                                                    t.Label ??
                                                                        ''
                                                                ).trim()
                                                            )}
                                                        </MarkDown>
                                                    </a>
                                                </li>
                                            ))}

                                        {/* Address */}
                                        {Address && (
                                            <li>
                                                <span
                                                    className={`text-sm font-text leading-6 ${iconColor} transition-all ease-in`}
                                                >
                                                    <MarkDown
                                                        components={{
                                                            p: 'span',
                                                            a: mdLink,
                                                        }}
                                                    >
                                                        {String(Address).trim()}
                                                    </MarkDown>
                                                </span>
                                            </li>
                                        )}

                                        {/* BusinessHour */}
                                        {BusinessHour && (
                                            <li>
                                                <span
                                                    className={`text-sm font-text leading-6 ${iconColor} transition-all ease-in`}
                                                >
                                                    <MarkDown
                                                        components={{
                                                            p: 'span',
                                                            a: mdLink,
                                                        }}
                                                    >
                                                        {String(
                                                            BusinessHour
                                                        ).trim()}
                                                    </MarkDown>
                                                </span>
                                            </li>
                                        )}

                                        {/* License */}
                                        {License && (
                                            <li>
                                                <span
                                                    className={`text-sm font-text leading-6 ${iconColor} transition-all ease-in`}
                                                >
                                                    <MarkDown
                                                        components={{
                                                            p: 'span',
                                                            a: mdLink,
                                                        }}
                                                    >
                                                        {String(License).trim()}
                                                    </MarkDown>
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Nicejob Footer */}
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div
                        data-nicejob-branding
                        className={`flex w-full sm:w-auto ${
                            Alignment === 'center'
                                ? 'justify-center'
                                : 'justify-center sm:justify-start'
                        }`}
                    >
                        <a
                            href="https://get.nicejob.com/product/sites"
                            className={`${brandBrightness} opacity-100 transition-all ease-in hover:opacity-80`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                className="h-6"
                                src="https://cdn.nicejob.com/assets/nicejoblogo-primary.svg"
                                alt="Crafted by nicejob"
                                width={128}
                                height={24}
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                    </div>

                    <div
                        className={`flex flex-wrap items-center justify-center gap-1 text-xs leading-5 sm:justify-end ${
                            Alignment === 'center' ? 'text-center' : 'text-left'
                        }`}
                    >
                        {resolvedCopyrightText && (
                            <span className={`${headerColor} opacity-80`}>
                                <MarkDown
                                    components={{
                                        p: 'span',
                                        a: 'span',
                                    }}
                                >
                                    {resolvedCopyrightText}
                                </MarkDown>
                            </span>
                        )}
                        {policyLinks.map(({ Name, Link }, index) => (
                            <React.Fragment key={String(Link).trim()}>
                                {(resolvedCopyrightText || index > 0) && (
                                    <span className={`text-xs ${headerColor}`}>
                                        |
                                    </span>
                                )}
                                <a
                                    href={String(Link).trim()}
                                    className={`${headerColor} opacity-80 transition-all ease-in hover:opacity-100`}
                                >
                                    <MarkDown
                                        components={{
                                            p: 'span',
                                            a: 'span',
                                        }}
                                    >
                                        {String(Name ?? '').trim()}
                                    </MarkDown>
                                </a>
                            </React.Fragment>
                        ))}

                        <a
                            href="#"
                            onClick={handleScrollToPageTop}
                            className={`ml-3 text-2xl ${headerColor} opacity-80 transition-all ease-in hover:opacity-100`}
                            aria-label="Back to Top"
                        >
                            <i className="fa-solid fa-circle-arrow-up" />
                        </a>
                    </div>
                </div>
            </div>

            {Engage !== 'hidden' && (
                <div className="nj-engage" data-position={Engage} />
            )}
        </footer>
    );
}
